import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth'
import { modulesApi } from '@/api/modules'
import type { ModuleConfig } from '@/types/modules'

/**
 * Hook to fetch and sync module config from the server.
 * Automatically refetches on window focus to pick up admin changes.
 */
export function useModuleConfig() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const updateModuleConfig = useAuthStore((state) => state.updateModuleConfig)

  const { data } = useQuery({
    queryKey: ['module-config'],
    queryFn: modulesApi.refreshModuleConfig,
    enabled: isAuthenticated,
    staleTime: 30_000, // Consider fresh for 30s
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  // Sync to auth store when data changes
  useEffect(() => {
    if (data) {
      updateModuleConfig(data)
    }
  }, [data, updateModuleConfig])
}

/**
 * Check if a specific module is enabled for the current tenant.
 * Reads from auth store (which is synced by useModuleConfig).
 */
export function useModuleEnabled(module: keyof ModuleConfig): boolean {
  const user = useAuthStore((state) => state.user)
  // Fail-closed: if user is null or module_config missing, return false
  return user?.module_config?.[module] ?? false
}
